import { useEffect, useState } from "react";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import { Grid, Typography } from "@mui/material";
import { SettingsService } from "../../../services/api";
import { useGlobalContext } from "../../../stores/Global";
import useRefresh from "../../../hooks/useRefresh";
import { useTranslation } from "react-i18next";
import { useMutationState } from "@tanstack/react-query";
import { useNavigate } from "react-router";

export default function ForwardEmailSettingsPage() {
  const { t } = useTranslation("Settings");
  const { context } = useGlobalContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [customerTicketEmails, setCustomerTicketEmails] = useState(false);
  const [customerTicketsRecipients, setCustomerTicketsRecipients] = useState<
    string[]
  >([]);
  const [firewallRecipients, setFirewallRecipients] = useState<string[]>([]);
  const [itemsRecipients, setItemsRecipients] = useState<string[]>([]);
  const [itemReminderRecipients, setItemReminderRecipients] = useState<
    string[]
  >([]);
  const [ftpRecipients, setFtpRecipients] = useState<string[]>([]);
  const [subscriptionsRecipients, setSubscriptionsRecipients] = useState<
    string[]
  >([]);
  const [tasksRecipients, setTasksRecipients] = useState<string[]>([]);
  const [ticketsRecipients, setTicketsRecipients] = useState<string[]>([]);
  const [usersRecipients, setUsersRecipients] = useState<string[]>([]);
  const [vouchersRecipients, setVouchersRecipients] = useState<string[]>([]);

  const [loading] = useMutationState({
    filters: { mutationKey: ["settings"] },
    select: (mutation) => mutation.state.status === "pending",
  });

  const handleSubmit = async () => {
    if (!context.settings) return;
    await SettingsService.updateSettings({
      appSettings: {
        ...context.settings,
      },
    });
    refresh();
  };

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!context.settings) return;
    setCustomerTicketEmails(context.settings.customerTicketEmails);
    setCustomerTicketsRecipients(
      context.settings.forwardEmails.customerTickets,
    );
    setFirewallRecipients(context.settings.forwardEmails.firewall);
    setFtpRecipients(context.settings.forwardEmails.ftp);
    setItemsRecipients(context.settings.forwardEmails.items);
    setTasksRecipients(context.settings.forwardEmails.tasks);
    setUsersRecipients(context.settings.forwardEmails.users);
    setTicketsRecipients(context.settings.forwardEmails.tickets);
    setVouchersRecipients(context.settings.forwardEmails.vouchers);
    setItemReminderRecipients(context.settings.forwardEmails.itemReminder);
    setSubscriptionsRecipients(context.settings.forwardEmails.subscriptions);
    console.log("settings", context.settings);
  }, [context.settings]);

  useEffect(() => {
    if (!context.settings) return;
    context.settings.customerTicketEmails = customerTicketEmails;
    context.settings.forwardEmails.customerTickets = customerTicketsRecipients;
    context.settings.forwardEmails.firewall = firewallRecipients;
    context.settings.forwardEmails.ftp = ftpRecipients;
    context.settings.forwardEmails.items = itemsRecipients;
    context.settings.forwardEmails.tasks = tasksRecipients;
    context.settings.forwardEmails.users = usersRecipients;
    context.settings.forwardEmails.tickets = ticketsRecipients;
    context.settings.forwardEmails.vouchers = vouchersRecipients;
    context.settings.forwardEmails.itemReminder = itemReminderRecipients;
    context.settings.forwardEmails.subscriptions = subscriptionsRecipients;
  }, [
    customerTicketEmails,
    customerTicketsRecipients,
    firewallRecipients,
    itemsRecipients,
    itemReminderRecipients,
    ftpRecipients,
    subscriptionsRecipients,
    tasksRecipients,
    ticketsRecipients,
    usersRecipients,
    vouchersRecipients,
  ]);

  const fields: {
    field: string;
    label: string;
    value: string[];
    onChange: (value: string[]) => void;
  }[] = [
    {
      field: "customerTicketsRecipients",
      label: t("CustomerTicketsRecipients"),
      value: customerTicketsRecipients,
      onChange: setCustomerTicketsRecipients,
    },
    {
      field: "ticketsRecipients",
      label: t("TicketsRecipients"),
      value: ticketsRecipients,
      onChange: setTicketsRecipients,
    },
    {
      field: "tasksRecipients",
      label: t("TasksRecipients"),
      value: tasksRecipients,
      onChange: setTasksRecipients,
    },
    {
      field: "usersRecipients",
      label: t("UsersRecipients"),
      value: usersRecipients,
      onChange: setUsersRecipients,
    },
    {
      field: "firewallRecipients",
      label: t("FirewallRecipients"),
      value: firewallRecipients,
      onChange: setFirewallRecipients,
    },
    {
      field: "itemsRecipients",
      label: t("ItemsRecipients"),
      value: itemsRecipients,
      onChange: setItemsRecipients,
    },
    {
      field: "itemReminderRecipients",
      label: t("ItemReminderRecipients"),
      value: itemReminderRecipients,
      onChange: setItemReminderRecipients,
    },
    {
      field: "ftpRecipients",
      label: t("FtpRecipients"),
      value: ftpRecipients,
      onChange: setFtpRecipients,
    },
    {
      field: "subscriptionsRecipients",
      label: t("SubscriptionsRecipients"),
      value: subscriptionsRecipients,
      onChange: setSubscriptionsRecipients,
    },
    {
      field: "vouchersRecipients",
      label: t("VouchersRecipients"),
      value: vouchersRecipients,
      onChange: setVouchersRecipients,
    },
  ];

  return (
    <FormTemplate
      title={t("ForwardEmails")}
      subtitle={t("EmailForwarding.Body")}
      disableCancelButton
      submitButtonText={t("Save")}
      disableEnter
      onSubmit={handleSubmit}
      confirm
      loading={loading}
      onCancel={handleClose}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormInput
            field="customerTicketEmails"
            label={t("CustomerTicketEmails")}
            value={customerTicketEmails}
            onChange={(value) => setCustomerTicketEmails(value as boolean)}
            type="switch"
            loading={loading}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("CustomerTicketEmailsHint")}
          </Typography>
        </Grid>
        {fields.map((item) => (
          <Grid key={item.field} size={{ xs: 12, md: 6 }}>
            <FormInput
              field={item.field}
              label={item.label}
              value={item.value}
              onChange={(value) => item.onChange(value as string[])}
              type="chip-list"
              rows={3}
              placeholder={t("EmailPlaceholder", { ns: "User" })}
              loading={loading}
            />
          </Grid>
        ))}
      </Grid>
    </FormTemplate>
  );
}
